import { supabase } from './supabase';

const API_BASE = '/api';

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = {};
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  return headers;
}

async function fetchJSON(path) {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

async function patchJSON(path, body) {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

async function postJSON(path, body) {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...authHeaders },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `API error: ${res.status}` }));
    throw new Error(err.error || `API error: ${res.status}`);
  }
  return res.json();
}

async function deleteJSON(path) {
  const authHeaders = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, { method: 'DELETE', headers: authHeaders });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

export const api = {
  dashboard: {
    chart: () => fetchJSON('/dashboard/chart'),
  },
  clinics: {
    list: () => fetchJSON('/clinics'),
    get: (id) => fetchJSON(`/clinics/${id}`),
    clinicians: (id) => fetchJSON(`/clinics/${id}/clinicians`),
    chart: (id) => fetchJSON(`/clinics/${id}/chart`),
    records: (id, month, year) => fetchJSON(`/clinics/${id}/records?month=${month}&year=${year}`),
    updateRecord: (clinicId, recordId, data) => patchJSON(`/clinics/${clinicId}/records/${recordId}`, data),
    updateClinician: (clinicId, clinicianId, data) => patchJSON(`/clinics/${clinicId}/clinicians/${clinicianId}`, data),
    recalculate: (id, month, year) => postJSON(`/clinics/${id}/recalculate?month=${month}&year=${year}`),
    range: (id, from, to) => fetchJSON(`/clinics/${id}/range?from=${from}&to=${to}`),
    trend: (id, from, to) => fetchJSON(`/clinics/${id}/trend?from=${from}&to=${to}`),
  },
  settings: {
    scoring: () => fetchJSON('/settings/scoring'),
    updateProfile: (firstName, lastName) => patchJSON('/settings/profile', { firstName, lastName }),
    updateEmail: (email) => patchJSON('/settings/email', { email }),
    updatePassword: (password) => patchJSON('/settings/password', { password }),
    clinics: () => fetchJSON('/settings/clinics'),
    createClinic: (name, email, firstName, lastName) => postJSON('/settings/clinics', { name, email, firstName, lastName }),
    updateClinicName: (clinicId, name) => patchJSON(`/settings/clinics/${clinicId}`, { name }),
    invite: (clinicId, data) => postJSON(`/settings/clinics/${clinicId}/invite`, data),
    cancelInvite: (clinicId, inviteId) => deleteJSON(`/settings/clinics/${clinicId}/invite/${inviteId}`),
    removeMember: (clinicId, memberId) => deleteJSON(`/settings/clinics/${clinicId}/members/${memberId}`),
  },
  clinicians: {
    list: () => fetchJSON('/clinicians'),
    get: (id) => fetchJSON(`/clinicians/${id}`),
    chart: (id) => fetchJSON(`/clinicians/${id}/chart`),
    records: (id, month, year) => fetchJSON(`/clinicians/${id}/records?month=${month}&year=${year}`),
  },
  billing: {
    createCheckoutSession: (clinicId, plan, couponCode) => postJSON('/billing/create-checkout-session', { clinicId, plan, couponCode }),
    createPortalSession: (clinicId) => postJSON('/billing/create-portal-session', { clinicId }),
    subscription: (clinicId) => fetchJSON(`/billing/subscription/${clinicId}`),
  },
  hipaaVault: {
    sync: (clinicId) => postJSON(`/hipaa-vault/sync/${clinicId}`),
    syncAll: () => postJSON('/hipaa-vault/sync-all'),
    testConnection: (clinicId) => postJSON(`/hipaa-vault/test/${clinicId}`),
    syncs: (clinicId) => fetchJSON(`/hipaa-vault/syncs/${clinicId}`),
  },
  dailyCaseload: {
    sync: (clinicId, days) => postJSON(`/daily-caseload/sync/${clinicId}${days ? `?days=${days}` : ''}`),
    syncAll: () => postJSON('/daily-caseload/sync-all'),
    breakdown: (clinicId, month, year) => fetchJSON(`/daily-caseload/${clinicId}?month=${month}&year=${year}`),
  },
  dailyAttendance: {
    sync: (clinicId, days) => postJSON(`/daily-attendance/sync/${clinicId}${days ? `?days=${days}` : ''}`),
    syncAll: () => postJSON('/daily-attendance/sync-all'),
  },
  admin: {
    users: () => fetchJSON('/admin/users'),
    invites: () => fetchJSON('/admin/invites'),
    deleteInviteByEmail: (email) => deleteJSON(`/admin/invites/by-email/${encodeURIComponent(email)}`),
    updateUser: (userId, data) => patchJSON(`/admin/users/${userId}`, data),
    setUserPassword: (userId, password) => patchJSON(`/admin/users/${userId}/password`, { password }),
    impersonateUser: (userId) => postJSON(`/admin/users/${userId}/impersonate`),
    deleteUser: (userId) => deleteJSON(`/admin/users/${userId}`),
    userClinics: (userId) => fetchJSON(`/admin/users/${userId}/clinics`),
    clinics: () => fetchJSON('/admin/clinics'),
    clinic: (clinicId) => fetchJSON(`/admin/clinics/${clinicId}`),
    createClinic: (data) => postJSON('/admin/clinics', data),
    updateClinic: (clinicId, data) => patchJSON(`/admin/clinics/${clinicId}`, data),
    deleteClinic: (clinicId) => deleteJSON(`/admin/clinics/${clinicId}`),
    addClinicMember: (clinicId, userId) => postJSON(`/admin/clinics/${clinicId}/members`, { userId }),
    removeClinicMember: (clinicId, userId) => deleteJSON(`/admin/clinics/${clinicId}/members/${userId}`),
    clinicians: () => fetchJSON('/admin/clinicians'),
    clinician: (clinicianId) => fetchJSON(`/admin/clinicians/${clinicianId}`),
    updateClinician: (clinicianId, data) => patchJSON(`/admin/clinicians/${clinicianId}`, data),
    updateClinicianRecord: (clinicianId, recordId, data) => patchJSON(`/admin/clinicians/${clinicianId}/records/${recordId}`, data),
    settings: () => fetchJSON('/admin/settings'),
    updateSettings: (data) => patchJSON('/admin/settings', data),
    previewMonthlySummary: () => postJSON('/admin/monthly-summary/preview'),
    syncStatus: () => fetchJSON('/admin/sync-status'),
  },
};
